import { groupBy, map, prop, uniqBy } from "ramda";

import { isClassicUiDisabled } from "@/redux/modules/config.ts";
import { ENTITY_TYPE, SEQUENCING_TYPE, UNKNOWN_GENOME_ID } from "@/settings/settings.js";

import { type Configuration, type Group, type Organism, type Project } from "../graphql.js";

function isSaveableType(entityType: string, parentType?: string) {
  // This type is a potential sourceType for saving
  return (
    (entityType === ENTITY_TYPE.VARIANT && parentType !== ENTITY_TYPE.MUTATION) || // SEL6-1954 specific to "Mutations's related variants"
    entityType === ENTITY_TYPE.VCF_VARIANT ||
    entityType === ENTITY_TYPE.PROTEIN ||
    entityType === ENTITY_TYPE.TRANSCRIPT ||
    entityType === ENTITY_TYPE.ORGANISM ||
    entityType === ENTITY_TYPE.GENE ||
    entityType === ENTITY_TYPE.PROTEIN_LOCATION ||
    entityType === ENTITY_TYPE.TRANSCRIPT_LOCATION ||
    entityType === ENTITY_TYPE.MUTATION ||
    entityType === ENTITY_TYPE.PROTEIN_MUTATION ||
    entityType === ENTITY_TYPE.GENE_MUTATION ||
    entityType === ENTITY_TYPE.ORTHOLOG ||
    entityType === ENTITY_TYPE.DIRECTIONAL_ORTHOLOG ||
    entityType === ENTITY_TYPE.BLAST_HIT || // SEL5-1631
    entityType === ENTITY_TYPE.SAMPLE ||
    entityType === ENTITY_TYPE.SAMPLE_PARENT ||
    entityType === ENTITY_TYPE.NUFEATURE ||
    entityType === ENTITY_TYPE.MARKER ||
    entityType === ENTITY_TYPE.MARKER_NUS ||
    entityType === ENTITY_TYPE.GENERIC_SEQ ||
    entityType === ENTITY_TYPE.CONTIG ||
    entityType === ENTITY_TYPE.PROBE ||
    entityType === ENTITY_TYPE.OTHER
  );
}

function canHaveGroupInClassicUiOption(entityType: string, configuration: Configuration) {
  return (
    !isClassicUiDisabled(configuration) &&
    (entityType === ENTITY_TYPE.VARIANT ||
      entityType === ENTITY_TYPE.PROTEIN ||
      entityType === ENTITY_TYPE.TRANSCRIPT ||
      entityType === ENTITY_TYPE.ORGANISM ||
      entityType === ENTITY_TYPE.GENE ||
      entityType === ENTITY_TYPE.PROTEIN_LOCATION ||
      entityType === ENTITY_TYPE.TRANSCRIPT_LOCATION ||
      entityType === ENTITY_TYPE.GENE_MUTATION ||
      entityType === ENTITY_TYPE.PROTEIN_MUTATION ||
      entityType === ENTITY_TYPE.MUTATION)
  );
}

function canStartAnalyzerFromSummary(entityType: string): boolean {
  return (
    entityType === ENTITY_TYPE.CONTIG ||
    entityType === ENTITY_TYPE.GENE ||
    entityType === ENTITY_TYPE.PROTEIN ||
    entityType === ENTITY_TYPE.TRANSCRIPT ||
    entityType === ENTITY_TYPE.ORGANISM ||
    entityType === ENTITY_TYPE.PROBE ||
    entityType === ENTITY_TYPE.OTHER
  );
}

function isSyntenySet(genomeSequencingType: Organism["sequencingType"] | undefined) {
  return (
    genomeSequencingType != undefined &&
    (genomeSequencingType === SEQUENCING_TYPE.PROTEIN_SYNTENY || genomeSequencingType === SEQUENCING_TYPE.PANGENOME)
  );
}

function canSendToSyntenyViewer(entityType: string, genomeSequencingType: keyof typeof SEQUENCING_TYPE): boolean {
  const isSupportedFeature =
    entityType === ENTITY_TYPE.GENE ||
    entityType === ENTITY_TYPE.PROTEIN ||
    entityType === ENTITY_TYPE.TRANSCRIPT ||
    entityType === ENTITY_TYPE.NUFEATURE;

  return isSyntenySet(genomeSequencingType) && isSupportedFeature;
}

function groupsByProject(groups: Group[] = []): Map<Project, Group[]> {
  const groupsByProjectId = groupBy<Group>((group) => String(group.project?.id))(groups ?? []);
  const uniqueProjects = uniqBy(
    (p) => prop("id", p),
    map((group) => group.project, groups),
  );
  const projectGroupsMap = new Map();
  uniqueProjects.forEach((project: Project) => {
    projectGroupsMap.set(project, groupsByProjectId[project.id]);
  });
  return projectGroupsMap;
}

function isGenomeViewerEnabledEntity(organism: Organism): boolean {
  return organism?.id !== UNKNOWN_GENOME_ID;
}

export {
  canHaveGroupInClassicUiOption,
  canSendToSyntenyViewer,
  canStartAnalyzerFromSummary,
  groupsByProject,
  isGenomeViewerEnabledEntity,
  isSaveableType,
  isSyntenySet,
};
